/*
 * Anchored positioning for floating overlays (tooltip / popover / menu):
 * resolve logical placements against reading direction, place a fixed float
 * beside its anchor with viewport-aware flipping, and keep it pinned while the
 * page scrolls or resizes.
 */

import { isRTL } from '../utilities/index.js';

// A logical or physical placement for an anchored float
export type Placement = 'top' | 'bottom' | 'left' | 'right' | 'start' | 'end';

// A resolved, physical placement (logical start / end mapped to left / right)
export type PhysicalPlacement = 'top' | 'bottom' | 'left' | 'right';

// Minimum distance an overlay keeps from the viewport edge
const VIEWPORT_MARGIN = 8;

/*
 * Resolve a logical placement to a physical one for the anchor's reading
 * direction: 'start' and 'end' become 'left' or 'right' as appropriate,
 * physical placements pass through untouched.
 */
export function resolvePlacement (placement: Placement, anchorEl: HTMLElement): PhysicalPlacement {
  if (placement !== 'start' && placement !== 'end') {
    return placement;
  }

  const rtl = isRTL(anchorEl);

  if (placement === 'start') {
    return rtl ? 'right' : 'left';
  }

  return rtl ? 'left' : 'right';
}

/*
 * Position a `position: fixed` float element relative to an anchor.
 * Accepts top / bottom / left / right plus the logical start / end, flips
 * to the opposite side when the preferred side would clip and finally
 * clamps inside the viewport.
 */
export function placeAnchored (
  anchorEl: HTMLElement,
  floatEl: HTMLElement,
  placement: Placement = 'bottom',
  gap = 8,
): PhysicalPlacement {
  const side = resolvePlacement(placement, anchorEl);

  const a  = anchorEl.getBoundingClientRect();
  const f  = floatEl.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Flip to the roomier side when the preferred side can't fit the float
  let resolved: PhysicalPlacement = side;

  if (side === 'top' && a.top - f.height - gap < VIEWPORT_MARGIN && a.bottom + f.height + gap < vh) {
    resolved = 'bottom';
  } else if (side === 'bottom' && a.bottom + f.height + gap > vh - VIEWPORT_MARGIN && a.top - f.height - gap > 0) {
    resolved = 'top';
  } else if (side === 'left' && a.left - f.width - gap < VIEWPORT_MARGIN && a.right + f.width + gap < vw) {
    resolved = 'right';
  } else if (side === 'right' && a.right + f.width + gap > vw - VIEWPORT_MARGIN && a.left - f.width - gap > 0) {
    resolved = 'left';
  }

  let top: number;
  let left: number;

  if (resolved === 'top')         top = a.top - f.height - gap;
  else if (resolved === 'bottom') top = a.bottom + gap;
  else                            top = a.top + (a.height - f.height) / 2;

  if (resolved === 'left')        left = a.left - f.width - gap;
  else if (resolved === 'right')  left = a.right + gap;
  else                            left = a.left + (a.width - f.width) / 2;

  left = Math.max(VIEWPORT_MARGIN, Math.min(vw - f.width - VIEWPORT_MARGIN, left));
  top  = Math.max(VIEWPORT_MARGIN, Math.min(vh - f.height - VIEWPORT_MARGIN, top));

  floatEl.style.left = `${Math.round(left)}px`;
  floatEl.style.top  = `${Math.round(top)}px`;

  // A position:fixed element is offset by its containing block whenever an
  // ancestor has a transform / filter / will-change (e.g. a card lifting on
  // hover). Measure where it actually landed and add the difference back, so
  // it stays pinned to the trigger regardless of transformed ancestors.
  const landed = floatEl.getBoundingClientRect();
  const dx     = left - landed.left;
  const dy     = top - landed.top;

  if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) {
    floatEl.style.left = `${Math.round(left + dx)}px`;
    floatEl.style.top  = `${Math.round(top + dy)}px`;
  }

  return resolved;
}

/*
 * Keep a fixed float pinned to its anchor while the page scrolls or
 * resizes, so an open dropdown doesn't drift away from its trigger. When
 * `onHidden` is given, it fires once the anchor has scrolled fully out of the
 * viewport, so an overlay can dismiss itself instead of floating over an
 * anchor the reader can no longer see. Returns a cleanup function.
 */
export function trackAnchor (
  anchorEl: HTMLElement,
  floatEl: HTMLElement,
  placement: Placement = 'bottom',
  gap = 8,
  onHidden: (() => void) | null = null,
): () => void {
  const reposition = (): void => {
    placeAnchored(anchorEl, floatEl, placement, gap);

    if (onHidden) {
      const r = anchorEl.getBoundingClientRect();

      if (r.bottom <= 0 || r.top >= window.innerHeight
        || r.right <= 0 || r.left >= window.innerWidth) {
        onHidden();
      }
    }
  };

  window.addEventListener('scroll', reposition, true); // capture → catches nested scrollers
  window.addEventListener('resize', reposition);

  return () => {
    window.removeEventListener('scroll', reposition, true);
    window.removeEventListener('resize', reposition);
  };
}
